<template>
  <div>
    <Transition name="slide-left-right">
      <div v-if="!showForm">
        <b-row v-if="showFilters" class="pt-2 pb-1">
          <b-col cols="6" class="d-flex align-items-center">
            <BreadcrumbMenu />
          </b-col>
          <b-col cols="6" class="pr-3 text-right">
            <button-filters
              @show-modal="$root.$emit('bv::show::modal', $event)"
              :filters-count="filtersCount"
              @clear-search-filters="clearSearchFilters"
            />
          </b-col>
        </b-row>

        <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
          <template v-slot:main-filters>
            <filters
              :unit-options="unitOptions"
              :unit-filter.sync="filters.units"
              :user-options="userOptions"
              :status-filter.sync="filters.status"
              :start-date-filter.sync="filters.startDate"
              :end-date-filter.sync="filters.endDate"
              :users-filter.sync="filters.users"
              :end-date-error="endDateError"
              :start-date-error="startDateError"
            />
          </template>
        </filter-modal>

        <b-card :class="{'mx-2': showFilters}" no-body>
          <b-row class="m-2" >
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search') + '...'"
                />
                <b-button
                  variant="primary"
                  @click="openForm()"
                  v-if="$can('create', 'CrisisForm') && showFilters"

                >
                  <span class="text-nowrap text-capitalize">{{ $tc("operational_security.crisis_form.add") }}</span>
                </b-button>
              </div>
            </b-col>
          </b-row>

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
          >
            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>

             <template #cell(icon)="data" v-if="isFromSlot">
              <div
                draggable="true"
                @dragstart="onDragStartCrisisFacilities(data)"
                @dragend="onDragEnd">
                <CustomIcon
                  :name="'DragArrow'"
                  :size="25"
                />
              </div>
            </template>

             <template #cell(reference_number)="data">
              <p class="mb-0">{{ data.item.reference_number }}</p>
              <b-badge :variant="data.item.last_status ? getStatusVariant(data.item.last_status.status.description) : getStatusVariant(data.item.last_status)">
                {{ formatStatus(data.item.last_status) }}
              </b-badge>
            </template>

            <template #head(actions)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #cell()="data">
              <div draggable="true" @dragstart="onDragStartCrisisFacilities(data)" @dragend="onDragEnd">
                {{ data.value }}
              </div>
            </template>

            <template #cell(actions)="data">
              <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item
                  v-if="$can('update', 'CrisisForm')"
                  @click="editForm(data.item)"
                >
                  <feather-icon icon="EditIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{
                    $t("actions.edit")
                  }}</span>
                </b-dropdown-item>
                <!-- <b-dropdown-item
                  v-if="showMailOption(data.item)"
                  @click="sendMail(data.item)"
                >
                  <feather-icon icon="MailIcon" />
                  <span class="align-middle ml-50 text-capitalize">
                    Enviar correo
                  </span>
                </b-dropdown-item> -->
              </b-dropdown>
            </template>
          </b-table>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted text-capitalize">
                  {{
                    $t("table.showing", {
                      from: dataMeta.from,
                      to: dataMeta.to,
                      of: dataMeta.of,
                    })
                  }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <Pagination
                  :totalList="totalList"
                  :currentPage="currentPage"
                  @updatePagination="update"
                  :perPage="perPage"
                />
              </b-col>
            </b-row>
          </div>
        </b-card>
      </div>
    </Transition>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
} from "bootstrap-vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { ref, getCurrentInstance } from "@vue/composition-api";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Pagination from "@/components/Pagination.vue";
import router from "@/router";

import useList from "./composables/useList";
import { useCrisisFormStatus } from './components/composables/useCrisisFormStatus';

import Filters from "./components/Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import {SLOT_TYPES, SOURCES} from "@/config/layoutConfig";
import store from "@/store";
import CustomIcon from '@/components/CustomIcon.vue'

export default {
  name: "CrisisForm",
  components: {
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BTable,
    Pagination,
    vSelect,
    ToastificationContent,
    Filters,
    FilterModal,
    ButtonFilters,
    CustomIcon
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
    isFromSlot: {
      type: Boolean,
      default: false,
    }
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      userSelected,
      getUserAssigned,
      getUserReview,
      getUserValidator,
      endDateError,
      startDateError
    } = useList();

    //COMPOSABLES
    const { formatStatus,getStatusVariant } = useCrisisFormStatus();

    const toast = useToast();


    const buttonExport = ref(false);
    const showForm = ref(false);
    const isEdit = ref(false);
    const dataToEdit = ref(null);

    const statusOptions = [
      { label: vm.$t("risk.status_in_progress"), value: "in_progress" },
      { label: vm.$t("risk.status_in_review"), value: "in_review" },
      { label: vm.$t("risk.status_approved"), value: "approved" },
      { label: vm.$t("risk.status_canceled"), value: "rejected" },
      { label: vm.$t("risk.status_reassigned"), value: "reopened" },
    ];

    const openForm = () => {
      router.push({ name: 'crisis_form_create'});
    }

    const editForm = async (item) => {
      router.push({
        name: 'crisis_form_edit',
        params: {
          id: item.id
        }
      });
    };

    const update = (val) => {
      currentPage.value = val;
    }

    const setSlotData = (type, name, data) => {
      store.dispatch('grid/setSlotData', {
        type,
        name,
        data: data || {},
        source: SOURCES.CRISIS_FORM,
      })
    }

    const onDragStartCrisisFacilities = (item) => {
      setSlotData(SLOT_TYPES.CRISIS_FACILITIES, 'crisis_facilities', item)
    }

    const onDragEnd = () => {
      store.dispatch('grid/resetSlotSelection')
    }

    const sendMail = (item) => {
      axios.get(`/v1/operational-security-crisis/${item.id}/send-email`)
        .then((response) => {
          const { data } = response;

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
        });
    }


    const showMailOption = (item) => {
      const { to_list_id,cc_list_id,bcc_list_id } = item;

      return (to_list_id && cc_list_id && bcc_list_id);
    }



    return {
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      isEdit,
      dataToEdit,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buttonExport,
      statusOptions,
      showForm,
      fetchList,
      refetchData,
      formatDateAssigned,
      buildFilters,
      clearSearchFilters,
      update,
      openForm,
      editForm,
      userSelected,
      getUserAssigned,
      getUserReview,
      getUserValidator,
      onDragStartCrisisFacilities,
      onDragEnd,
      sendMail,
      showMailOption,
      formatStatus,
      getStatusVariant,
      userOptions,
      endDateError,
      startDateError
    };
  },
};
</script>

<style>
.slide-right-left-enter-active,
.slide-right-left-leave-active {
  transition: transform 0.2s ease;
}

.slide-right-left-enter-from {
  transform: translateX(100%);
}

.slide-right-left-enter-to {
  transform: translateX(0%);
}

.slide-right-left-leave-from {
  transform: translateX(0%);
}

.slide-right-left-leave-to {
  transform: translateX(100%);
}

.slide-left-right-enter-active,
.slide-left-right-leave-active {
  transition: transform 0.2s ease;
}

.slide-left-right-enter-from {
  transform: translateX(-100%);
}

.slide-left-right-enter-to {
  transform: translateX(0%);
}

.slide-left-right-leave-from {
  transform: translateX(0%);
}

.slide-left-right-leave-to {
  transform: translateX(-100%);
}

</style>

